import { EditableText, defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { Col, Container, Grid } from "@components/layout/Grid"
import { NRMAC, RACVC, RACQC, RAAC, RACC, RACTC, AANTC, Logo } from "@components/atoms/Logo"
import { useIsMobile } from "@hooks/useViewportDerived"
import { cn } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef } from "react"
import { useClubs } from "@hooks/queries"
import { Heading } from "@components/atoms/Heading"
import { Button } from "@components/atoms/Button"
import { Typography } from "@components/atoms/Typography"

export const meta: BlockMeta = {
  title: "Buy an IDP",
  category: "common",
  icon: "embed-photo",
  keywords: ["accordion"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/buy-an-idp", (props) => {
  const isMobile = useIsMobile()
  const clubs = useClubs(undefined)
  const hydrating = useHydrating()

  const getLogo = (name: string) => {
    switch (name) {
      case "NRMA":
        return <NRMAC />

      case "RACV":
        return <RACVC />

      case "RACQ":
        return <RACQC />

      case "RAA":
        return <RAAC />

      case "RAC":
        return <RACC />

      case "RACT":
        return <RACTC />

      case "AANT":
        return <AANTC />

      default:
        return <Logo />
    }
  }

  return (
    <Container>
      <Grid>
        {!isMobile && !env.admin && <Col initial={12} tablet={1} laptop={2} />}
        <Col initial={12} tablet={env.admin ? 12 : 10} laptop={env.admin ? 12 : 8}>
          <IDPContainer>
            <TitleWrap>
              <EditableText as="h3" store="title" placeholder="Title..." />
              <Button
                color="yellow"
                size="medium"
                icon="ARROW_UP_RIGHT"
                iconStyle="right"
                href="https://www.internationaldrivingpermitsonline.com.au/"
                target="_blank"
              >
                Apply online
              </Button>
            </TitleWrap>

            <EditableText as="p" store="content" placeholder="Content..." />

            {!hydrating &&
              clubs.data?.siteSettings?.themeSiteSettings?.clubs?.map((club, i) => (
                <Club key={i}>
                  {!isMobile && <Left>{getLogo(club!.name!)}</Left>}
                  {isMobile && (
                    <FlexRow>
                      <MobileLogo>{getLogo(club!.name!)}</MobileLogo>
                      <FlexCol>
                        <Heading as="h4" variant="h4">
                          {club?.name}
                        </Heading>
                        {club?.location && (
                          <Typography
                            as="span"
                            variant="caps"
                            dangerouslySetInnerHTML={{
                              __html: isMobile ? club.location.replace(" / ", "<br />") : club.location,
                            }}
                          />
                        )}
                      </FlexCol>
                    </FlexRow>
                  )}

                  {!isMobile && (
                    <Right>
                      <FlexCol>
                        <Heading as="h4" variant="h4">
                          {club?.name}
                        </Heading>
                        {club?.location && (
                          <Typography
                            as="span"
                            variant="caps"
                            dangerouslySetInnerHTML={{
                              __html: isMobile ? club.location.replace(" / ", "<br />") : club.location,
                            }}
                          />
                        )}
                      </FlexCol>

                      <ButtonGroup>
                        {club?.idpStoreFinderLink && (
                          <Button
                            color="yellow"
                            size="medium"
                            icon="MOTORING_ALT"
                            iconStyle="right"
                            href={club.idpStoreFinderLink}
                            target="_blank"
                          >
                            Locate branch
                          </Button>
                        )}

                        {club?.alternatives?.map((alterantive, x) => {
                          if (!alterantive?.link) return <></>
                          return (
                            <Button
                              color="white"
                              size="medium"
                              icon="ARROW_UP_RIGHT"
                              iconStyle="right"
                              key={x}
                              href={alterantive.link}
                              target="_blank"
                            >
                              {alterantive?.label}
                            </Button>
                          )
                        })}
                      </ButtonGroup>
                    </Right>
                  )}

                  {isMobile && (
                    <>
                      <ButtonGroup>
                        {club?.idpStoreFinderLink && (
                          <Button
                            color="yellow"
                            size="medium"
                            icon="MOTORING_ALT"
                            iconStyle="right"
                            href={club.idpStoreFinderLink}
                            target="_blank"
                          >
                            Locate branch
                          </Button>
                        )}

                        {club?.alternatives?.map((alterantive, x) => {
                          if (!alterantive?.link) return <></>
                          return (
                            <Button
                              color="white"
                              size="medium"
                              icon="ARROW_UP_RIGHT"
                              iconStyle="right"
                              key={x}
                              href={alterantive.link}
                              target="_blank"
                            >
                              {alterantive?.label}
                            </Button>
                          )
                        })}
                      </ButtonGroup>
                    </>
                  )}
                </Club>
              ))}
          </IDPContainer>
        </Col>
      </Grid>
    </Container>
  )
})

const IDPContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-light-grey rounded-md p-6 flex flex-col gap-4 [&_h3]:type-title-m [&_h3]:my-0 [&>p]:type-body-s",
      className,
    )}
    {...props}
  />
))
IDPContainer.displayName = "IDPContainer"

const TitleWrap = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex justify-between items-center", className)} {...props} />
))
TitleWrap.displayName = "TitleWrap"

const Club = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("border-t border-t-grey py-4 flex gap-6 flex-col [&_h4]:mt-2 md:flex-row", className)}
    {...props}
  />
))
Club.displayName = "Club"

const ButtonGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex gap-4 mt-4 flex-col md:flex-row md:flex-wrap", className)} {...props} />
))
ButtonGroup.displayName = "ButtonGroup"

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-white rounded-[6px] overflow-hidden min-w-[120px] p-4 [&_svg]:w-[120px] [&_svg]:h-[120px]",
      className,
    )}
    {...props}
  />
))
Left.displayName = "Left"

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col justify-between", className)} {...props} />
))
Right.displayName = "Right"

const FlexCol = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1", className)} {...props} />
))
FlexCol.displayName = "FlexCol"

const FlexRow = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-row", className)} {...props} />
))
FlexRow.displayName = "FlexRow"

const MobileLogo = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-white rounded-[6px] overflow-hidden min-w-[56px] w-[56px] h-[56px] mr-4 [&_svg]:w-[56px] [&_svg]:h-[56px]",
      className,
    )}
    {...props}
  />
))
MobileLogo.displayName = "MobileLogo"
